import type { AppDispatch } from "app/store";
import { TEMPLATE_TYPE_DIGITAL_FORM } from "constants/templates";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import type {
  ConversionCatalogEntry,
  FormConversionConfirm,
  FormConversionConfirmed,
  FormConversionStart,
  FormConversionStatus,
  MergeTagSuggestionField,
  MergeTagSuggestionsStatus
} from "./types/form-conversion.type";
import type { PromptOutputFormat } from "./types/prompts.type";
import {
  TextTemplate,
  TextTemplateFilters,
  TextTemplateInput,
  TextTemplateOwner,
  TextTemplateOwnershipTransfer,
  TextTemplatesResponse
} from "./types/text-templates.type";

/**
 * Lazy body fetch projection (ENG-1486). The templates list is fetched slim;
 * each row fetches its body with this explicit include on first expand.
 * Explicit include only — the backend queryset optimizer silently no-ops on
 * RestQL exclusion syntax (SN-10344).
 */
export const TEMPLATE_BODY_QUERY =
  "{id,text,subject_template,email_design_json}";

/** ENG-2277: a surface's picker row for each digital Form — no body, no owner. The backend defers
 * the body columns when the projection names none of them (references/views/text_template.py). */
export const DIGITAL_FORMS_PICKER_QUERY =
  "{id,name,type,category{id,title},placements}";

/** ENG-2277: what opening a digital Form needs: its body, surfaces and print settings. */
export const DIGITAL_FORM_OPEN_QUERY =
  "{id,name,text,placements,form_settings,category{id,title}}";

/** ENG-2277: the body projection plus a Form's surfaces and print settings. Requested only with
 * HTML_FORMS_FEATURE on, so a backend without those fields is never asked for them. */
export const TEMPLATE_BODY_WITH_FORM_QUERY =
  "{id,text,subject_template,email_design_json,placements,form_settings}";

/** Full projection incl. bodies — for consumers that need complete template
 * data immediately (e.g. forms-workflow-dialog fetches one full template on
 * selection, form-templates-with-custom-field's picker has no row-expand
 * moment to lazy-load behind). Not used by the settings list; see
 * TEMPLATES_LIST_QUERY below. */
export const TEMPLATES_QUERY =
  "{id,is_public,text,subject_template,type,name,category,owner,email_design_json}";

/**
 * Slim list projection (ENG-1486): bodies (`text`, `subject_template`,
 * `email_design_json`) are intentionally excluded — fetching 25 full HTML
 * bodies froze /settings/templates (route-change p75 5.9s). Each row
 * lazy-fetches its body on first expand via TEMPLATE_BODY_QUERY.
 * `text_preview` is a server-built ~200-char plain-text snippet that keeps
 * the collapsed-row preview line working.
 */
export const TEMPLATES_LIST_QUERY =
  "{id,is_public,type,name,category,owner,text_preview}";

/**
 * SN-19710: the list projection plus `allow_others_to_edit`. Selected only when
 * `TEMPLATE_SHARED_EDITING_FEATURE` is on — RestQL raises FieldNotFound for a
 * field the backend does not have yet, which would blank the whole list.
 */
export const TEMPLATES_LIST_QUERY_WITH_SHARED_EDITING =
  "{id,is_public,allow_others_to_edit,type,name,category,owner,text_preview}";

/**
 * ENG-2277: the Digital Forms list also shows where each form is used and when it was last edited.
 * Its section exists only with HTML_FORMS_FEATURE on, so an older backend is never asked for them.
 */
const DIGITAL_FORMS_LIST_FIELDS = ",placements,updated_at";

/**
 * ENG-2470: what the Digital Forms list's "Copies" and "Not offered anywhere" picks read about every
 * form matching the list's filters, including the pages not scrolled to yet. No bodies.
 */
export const DIGITAL_FORMS_BULK_DELETE_QUERY =
  "{id,name,owner,placements,updated_at}";

export const getTemplatesListQuery = (
  sharedEditingEnabled: boolean,
  isDigitalForms = false
) => {
  const query = sharedEditingEnabled
    ? TEMPLATES_LIST_QUERY_WITH_SHARED_EDITING
    : TEMPLATES_LIST_QUERY;
  return isDigitalForms
    ? query.replace(/\}$/, `${DIGITAL_FORMS_LIST_FIELDS}}`)
    : query;
};

/**
 * `getTextTemplate` (single-id body fetch) and the list endpoints below share
 * one tag type but are scoped by id so they invalidate independently
 * (ENG-1486). A flat `"TextTemplates"` tag meant creating one template
 * refetched every currently-expanded row's cached body too — each refetch's
 * new `defaultValues` reset the row's form, discarding unsaved edits. Only
 * `{ type: "TextTemplates", id: "LIST" }` should be invalidated on
 * create/delete; a per-id body cache is kept fresh via the explicit
 * `upsertTemplateBodyCacheOnSave` upsert on save instead.
 */
const LIST_TAG = { type: "TextTemplates" as const, id: "LIST" as const };
const OWNERS_TAG = { type: "TextTemplates" as const, id: "OWNERS" as const };
/**
 * ENG-2277: the surfaces' Digital Form pickers, apart from LIST so a Digital Form's save can refresh
 * them without refetching every template list (updateTextTemplate invalidates nothing on purpose).
 * A form placed on a surface is then in that surface's picker the next time it opens, rather than after
 * the picker's five-minute cache.
 */
export const DIGITAL_FORM_PICKER_TAG = {
  type: "TextTemplates" as const,
  id: "DIGITAL_FORM_PICKERS" as const
};

/** One page of a conversion job as the server drew it, and how long its picture may take to arrive. */
export type FormConversionPageArgs = {
  jobId: string;
  /** From 1. */
  page: number;
  timeoutMs: number;
};

export const textTemplatesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getTextTemplate: builder.query<
      TextTemplate,
      { id: number; query?: string }
    >({
      query: ({ id, query = "{*}" }) =>
        `/text-templates/${id}/?query=${query}&limit=300&ordering=name`,
      providesTags: (_result, _error, { id }) => [{ type: "TextTemplates", id }]
    }),
    getTextTemplates: builder.query<TextTemplatesResponse, TextTemplateFilters>(
      {
        query: ({ query = "{*}", limit = 300, offset = 0, ...filters }) =>
          `/text-templates/?${formatValidQueryParamUrlCommaSeparated({
            ...filters,
            limit,
            offset
          })}&query=${query}&ordering=name`,
        providesTags: [LIST_TAG]
      }
    ),
    // ENG-2277: the Digital Forms placed on one surface, for its form picker. Cached for five
    // minutes, as every open of the same surface asks for the same list (tech plan D17).
    getDigitalForms: builder.query<
      TextTemplatesResponse,
      { placement: number }
    >({
      query: ({ placement }) =>
        `/text-templates/?type=${TEMPLATE_TYPE_DIGITAL_FORM}&placement=${placement}&limit=300&offset=0&query=${DIGITAL_FORMS_PICKER_QUERY}&ordering=name`,
      providesTags: [LIST_TAG, DIGITAL_FORM_PICKER_TAG],
      keepUnusedDataFor: 300
    }),
    // ENG-2277: PDF-to-Form conversion (suno_backend/references/form_conversion/SCHEMA.md). Confirming
    // and suggesting are paid AI calls: each has its own timeout, and mutations are never retried.
    // The import dialog explains every failure of these itself, so the app-wide network and timeout
    // toast stays out of it: it would repeat the dialog's message, and fire on every transient poll.
    // A conversion starts in two requests: the upload, which the server checks and draws, and the
    // confirm, sent once the person has looked at the server's pictures and said the form is blank.
    // The upload is the PDF and its layout (14 MB at most; the browser's own page pictures are never
    // sent), so its timeout comes from the file's size (uploadTimeoutFor).
    startFormConversion: builder.mutation<
      FormConversionStart,
      { formData: FormData; timeoutMs: number }
    >({
      query: ({ formData, timeoutMs }) => ({
        url: "/text-templates/conversions/",
        method: "POST",
        body: formData,
        timeout: timeoutMs
      }),
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    // One page as the server drew it: the picture that goes to the AI service. It is read here with
    // the app's own headers and shown from memory, so the sign-in token is never in an address the
    // browser fetches for an <img>. Nothing keeps it once it has been handed over: not the browser's
    // cache (no-store) and not this one (keepUnusedDataFor 0). A picture is 1.5 MB at most and the
    // import dialog fetches a few at a time, so it says how long each may take (as the upload does);
    // one that runs out of time is asked for once more, then left for the person to retry.
    getFormConversionPage: builder.query<Blob, FormConversionPageArgs>({
      query: ({ jobId, page, timeoutMs }) => ({
        url: `/text-templates/conversions/${jobId}/pages/${page}/`,
        cache: "no-store",
        timeout: timeoutMs,
        // fetchBaseQuery runs responseHandler for error responses too, then branches on
        // response.ok: a refusal's JSON must not be handed on as a picture.
        responseHandler: (response) =>
          response.ok ? response.blob() : response.json().catch(() => null)
      }),
      keepUnusedDataFor: 0,
      extraOptions: { suppressGlobalErrorToast: true, maxRetries: 1 }
    }),
    confirmFormConversion: builder.mutation<
      FormConversionConfirmed,
      { jobId: string; body: FormConversionConfirm }
    >({
      query: ({ jobId, body }) => ({
        url: `/text-templates/conversions/${jobId}/confirm/`,
        method: "POST",
        body,
        timeout: 30_000
      }),
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    getFormConversion: builder.query<FormConversionStatus, { jobId: string }>({
      query: ({ jobId }) => `/text-templates/conversions/${jobId}/`,
      keepUnusedDataFor: 0,
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    cancelFormConversion: builder.mutation<void, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/text-templates/conversions/${jobId}/`,
        method: "DELETE",
        timeout: 15_000
      }),
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    // Starts a job: the AI call runs on a worker, and getMergeTagSuggestions follows it.
    suggestMergeTags: builder.mutation<
      { job_id: string; status: "queued" },
      { fields: MergeTagSuggestionField[]; catalog: ConversionCatalogEntry[] }
    >({
      query: (body) => ({
        url: "/text-templates/merge-tag-suggestions/",
        method: "POST",
        body,
        timeout: 15_000
      }),
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    getMergeTagSuggestions: builder.query<
      MergeTagSuggestionsStatus,
      { jobId: string }
    >({
      // A poll reads the cache and answers at once. One that hangs is dropped and asked again, so it
      // cannot hold the polling past its deadlines.
      query: ({ jobId }) => ({
        url: `/text-templates/merge-tag-suggestions/${jobId}/`,
        timeout: 10_000
      }),
      keepUnusedDataFor: 0,
      extraOptions: { suppressGlobalErrorToast: true }
    }),
    getTextTemplatesPaginated: createPaginatedEndpoint<
      TextTemplateFilters,
      TextTemplate[]
    >(builder, {
      queryFn: ({ query = "{*}", limit = 25, offset = 0, ...filters }) =>
        `/text-templates/?${formatValidQueryParamUrlCommaSeparated({
          ...filters,
          limit,
          offset
        })}&query=${query}&ordering=name`,
      providesTags: [LIST_TAG],
      transformResponseFn: (response: TextTemplate[]) => response
    }),
    getTextTemplatesCount: builder.query<
      number,
      Omit<TextTemplateFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/text-templates/count/?${formatValidQueryParamUrlCommaSeparated({
          ...filters
        })}`,
      providesTags: [LIST_TAG]
    }),
    addTextTemplate: builder.mutation<TextTemplate, TextTemplateInput>({
      query: (templateData) => ({
        url: "/text-templates/",
        method: "POST",
        body: templateData
      }),
      invalidatesTags: [LIST_TAG],
      onQueryStarted: upsertTemplateBodyCacheOnSave
    }),
    updateTextTemplate: builder.mutation<TextTemplate, TextTemplateInput>({
      query: ({ id, ...templateData }) => ({
        url: `/text-templates/${id}/`,
        method: "PATCH",
        body: templateData
      }),
      // We don't need invalidation here because we manually update the specific Template. Otherwise, setPageItemDataState breaks
      // invalidatesTags: ["TextTemplates"]
      onQueryStarted: upsertTemplateBodyCacheOnSave
    }),
    /** Same as updateTextTemplate but no tag invalidation - for preview flow to avoid refetch */
    patchTextTemplateForPreview: builder.mutation<
      TextTemplate,
      TextTemplateInput
    >({
      query: ({ id, ...templateData }) => ({
        url: `/text-templates/${id}/`,
        method: "PATCH",
        body: templateData
      }),
      onQueryStarted: upsertTemplateBodyCacheOnSave
    }),
    /** Same as addTextTemplate but no tag invalidation - for preview flow to avoid refetch */
    addTextTemplateForPreview: builder.mutation<
      TextTemplate,
      TextTemplateInput
    >({
      query: (templateData) => ({
        url: "/text-templates/",
        method: "POST",
        body: templateData
      }),
      onQueryStarted: upsertTemplateBodyCacheOnSave
    }),
    deleteTextTemplate: builder.mutation<string, number>({
      query: (id) => ({
        url: `/text-templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: [LIST_TAG]
    }),
    /** Everyone who owns a template in the practice, deactivated staff included (business owners and Suno). */
    getTextTemplateOwners: builder.query<TextTemplateOwner[], void>({
      query: () => "/text-templates/owners/",
      providesTags: [OWNERS_TAG]
    }),
    /** Makes `to_owner` the owner of every template `from_owner` has in the practice. */
    transferTextTemplateOwnership: builder.mutation<
      { transferred: number },
      TextTemplateOwnershipTransfer
    >({
      query: (body) => ({
        url: "/text-templates/transfer-ownership/",
        method: "POST",
        body
      }),
      // Not LIST_TAG: the Settings list reloads every page itself after a transfer, and a lazy page fetch fired
      // while a tag-invalidation refetch of the same entry is in flight returns the old rows (root CLAUDE.md).
      // The caller invalidates the lists once its own reload is done (`invalidateTextTemplateLists`).
      invalidatesTags: [OWNERS_TAG]
    }),
    autoCompleteTextTemplates: builder.mutation<
      string,
      {
        name: string;
        text: string;
        type?: number;
        clinic_id?: number;
        user_prompt?: string;
        format?: PromptOutputFormat;
      }
    >({
      query: (templateData) => ({
        url: "/text-templates/complete",
        method: "POST",
        body: templateData
      })
    })
  })
});

/** What a save can change, in the shape every projection holds it. `owner` and `category` are left
 * out: a projection may hold them expanded, and the save answers with their ids. */
const SAVED_FIELDS = [
  "text",
  "subject_template",
  "email_design_json",
  "name",
  "type",
  "is_public",
  "allow_others_to_edit",
  "placements",
  "form_settings"
] as const satisfies readonly (keyof TextTemplate)[];

type TextTemplatesState = Parameters<
  typeof textTemplatesApi.util.selectInvalidatedBy
>[0];

/**
 * Keep the per-id body cache (getTextTemplate + TEMPLATE_BODY_QUERY) in sync
 * after a create or save. With the slim list (ENG-1486) that cache is the only
 * client copy of a template's body — a stale entry would revert the form to
 * the pre-save body on the next list-driven reset, and a missing entry after
 * POST would make the new row's first expand refetch a body the create
 * response already carried.
 */
async function upsertTemplateBodyCacheOnSave(
  _templateData: TextTemplateInput,
  {
    dispatch,
    getState,
    queryFulfilled
  }: {
    dispatch: AppDispatch;
    getState: () => unknown;
    queryFulfilled: Promise<{ data: TextTemplate }>;
  }
) {
  try {
    const { data } = await queryFulfilled;
    const id = Number(data.id);
    dispatch(
      textTemplatesApi.util.upsertQueryData(
        "getTextTemplate",
        { id, query: TEMPLATE_BODY_QUERY },
        data
      )
    );
    // ENG-2277: the same template can be cached under other projections too (a Form's row asks for
    // its print settings with the body). Each copy that is cached takes what was saved; one left
    // behind would put the old text back the next time its form resets.
    textTemplatesApi.util
      .selectInvalidatedBy(getState() as TextTemplatesState, [
        { type: "TextTemplates", id }
      ])
      .forEach(({ endpointName, originalArgs }) => {
        if (
          endpointName !== "getTextTemplate" ||
          originalArgs.query === TEMPLATE_BODY_QUERY
        )
          return;
        dispatch(
          textTemplatesApi.util.updateQueryData(
            "getTextTemplate",
            originalArgs,
            (cached) => {
              SAVED_FIELDS.forEach((field) => {
                if (field in cached && field in data)
                  Object.assign(cached, { [field]: data[field] });
              });
            }
          )
        );
      });
  } catch {
    // Save failed — error handling/snackbar lives with the caller.
  }
}

/**
 * ENG-2277: fetches one page's picture for a caller that holds the pictures itself. The import dialog
 * does: it keeps them above the part of itself that the shared Dialog rebuilds when the window is
 * resized, so a picture is fetched once for a look at a job and not again on every rebuild. Always
 * asked of the server, never a kept copy: the server counts a page as looked at only when it has
 * served it. The picture stays in the cache only until it is handed over.
 */
export const fetchFormConversionPage = (
  dispatch: AppDispatch,
  args: FormConversionPageArgs
) => {
  const request = dispatch(
    textTemplatesApi.endpoints.getFormConversionPage.initiate(args, {
      forceRefetch: true
    })
  );
  return {
    /** Resolves with the picture; rejects with the server's refusal, or the failure to reach it. */
    picture: request.unwrap().finally(() => request.unsubscribe()),
    /** Stops the request: nobody is waiting for the picture any more. */
    abort: () => request.abort()
  };
};

export const {
  useGetTextTemplateQuery,
  useLazyGetTextTemplateQuery,
  useGetTextTemplatesQuery,
  useLazyGetTextTemplatesQuery,
  useGetDigitalFormsQuery,
  useStartFormConversionMutation,
  useConfirmFormConversionMutation,
  useGetFormConversionQuery,
  useCancelFormConversionMutation,
  useSuggestMergeTagsMutation,
  useLazyGetMergeTagSuggestionsQuery,
  useGetTextTemplatesPaginatedQuery,
  useGetTextTemplatesCountQuery,
  useAddTextTemplateMutation,
  useUpdateTextTemplateMutation,
  useDeleteTextTemplateMutation,
  useAutoCompleteTextTemplatesMutation,
  usePatchTextTemplateForPreviewMutation,
  useAddTextTemplateForPreviewMutation,
  useGetTextTemplateOwnersQuery,
  useTransferTextTemplateOwnershipMutation
} = textTemplatesApi;

/** Refetches every template list and count that is on screen anywhere (pickers, counts, other sections). */
export const invalidateTextTemplateLists = () =>
  textTemplatesApi.util.invalidateTags([LIST_TAG]);
